import React, { useEffect, useState } from "react";
import axios from "axios";
import { Radio, Heart, ExternalLink, Quote, Radio as RadioIcon, CalendarClock } from "lucide-react";
import { Header } from "../components/Header";
import { InstallPrompt } from "../components/InstallPrompt";
import { IosInstallGuide } from "../components/IosInstallGuide";
import { AndroidInstallGuide } from "../components/AndroidInstallGuide";
import { ShareWhatsAppButton } from "../components/ShareWhatsAppButton";
import { CarHint } from "../components/CarHint";
import { VintageRadio } from "../components/VintageRadio";
import { Schedule } from "../components/Schedule";
import { SectionLabel } from "../components/SectionLabel";
import { SleepTimer } from "../components/SleepTimer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK = {
  name: "MADRETERRA radio",
  description:
    "La radio di Madre Terra — preghiera, spiritualità e devozione a San Francesco d'Assisi, in diretta in tutto il mondo, 24 ore su 24.",
  hls_url: "https://mcv2.streams.gr/stream/madreterra/index.m3u8",
  channel_url: "https://mcv2.streams.gr/channel/madreterra",
  radio_url: "https://sp3.streams.ovh:8092/stream",
  radio_name: "Radio Madre Terra",
  website_url: "https://www.radiomadreterra.it",
  donate_url:
    "https://www.paypal.com/donate/?business=Z8GL3NH8T78RL&no_recurring=0&item_name=Sostieni+Radio+Madre+Terra&currency_code=EUR",
  poster_url: "https://mcv2.streams.gr/users/madreterra/thumbnails/default.jpg",
};

export default function Home() {
  const [info, setInfo] = useState(FALLBACK);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/stream-info`)
      .then((res) => {
        if (!cancelled && res.data) setInfo(res.data);
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const now = new Date();
  const dateStr = now.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className="min-h-screen flex flex-col" data-testid="home-page">
      <Header info={info} />

      <main className="relative z-10 flex-1 px-6 sm:px-10 lg:px-14 pt-10 sm:pt-14 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Title block */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 mb-8 sm:mb-10">
            <div className="lg:col-span-7 mt-rise">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.28em] mb-5"
                style={{
                  background: "rgba(212,160,91,0.10)",
                  color: "var(--mt-amber-bright)",
                  border: "1px solid rgba(212,160,91,0.30)",
                }}
                data-testid="channel-eyebrow"
              >
                <RadioIcon size={13} strokeWidth={2.2} /> canale ufficiale
              </div>

              <h1
                className="font-display tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
                style={{ color: "var(--mt-cream)" }}
                data-testid="page-title"
              >
                MADRETERRA <span style={{ color: "var(--mt-amber)" }}>radio</span>
              </h1>

              <p
                className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed"
                style={{ color: "var(--mt-text-muted)" }}
                data-testid="page-subtitle"
              >
                {info.description}
              </p>
            </div>

            <div className="lg:col-span-5 lg:pt-2 mt-rise mt-rise-delay-1">
              <div
                className="rounded-2xl border p-5 sm:p-6"
                style={{
                  borderColor: "var(--mt-line)",
                  background: "rgba(20,17,13,0.55)",
                  backdropFilter: "blur(14px)",
                }}
                data-testid="now-card"
              >
                <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                  <div
                    className="text-[11px] uppercase tracking-[0.28em]"
                    style={{ color: "var(--mt-text-muted)" }}
                  >
                    Oggi · {dateStr}
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                      style={{
                        background: "rgba(184,85,48,0.20)",
                        color: "#f4a98a",
                        border: "1px solid rgba(184,85,48,0.45)",
                      }}
                    >
                      <span
                        className="mt-live-dot w-1.5 h-1.5 rounded-full"
                        style={{ background: "#f4a98a" }}
                      />
                      On Air
                    </div>
                  </div>
                </div>

                <Quote
                  size={20}
                  strokeWidth={1.8}
                  style={{ color: "var(--mt-amber)" }}
                />
                <p
                  className="font-display italic text-lg sm:text-xl mt-2 leading-snug"
                  style={{ color: "var(--mt-cream)" }}
                >
                  «Laudato si', mi' Signore, per sora nostra matre Terra.»
                </p>
                <p
                  className="text-xs mt-3 tracking-wide"
                  style={{ color: "var(--mt-text-muted)" }}
                >
                  — San Francesco d'Assisi · Cantico delle Creature
                </p>
              </div>
            </div>
          </div>

          {/* Radio Player (audio) */}
          <SectionLabel
            icon={<RadioIcon size={12} strokeWidth={2.4} />}
            testid="section-label-radio"
          >
            Web Radio
          </SectionLabel>
          <div className="mt-rise mt-rise-delay-2 mb-6 sm:mb-8 mt-3">
            <VintageRadio
              src={info.radio_url}
              name={info.radio_name || "Radio Madre Terra"}
            />
          </div>

          {/* Daily schedule */}
          <SectionLabel
            icon={<CalendarClock size={12} strokeWidth={2.4} />}
            testid="section-label-schedule"
          >
            Palinsesto del giorno
          </SectionLabel>
          <div className="mt-3 mt-rise mt-rise-delay-3">
            <Schedule />
          </div>

          {/* Below player meta strip */}
          <div className="mt-6 sm:mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-rise mt-rise-delay-4">
            <ShareWhatsAppButton variant="tile" />
            <InfoTile
              icon={<Radio size={16} strokeWidth={2.2} />}
              label="Radio Madre Terra"
              value="Web radio · 24/7"
              href={info.website_url}
              testid="info-radio"
            />
            <InfoTile
              icon={<Heart size={16} strokeWidth={2.2} />}
              label="Sostieni"
              value="Dona via PayPal"
              href={info.donate_url}
              testid="info-donate"
              accent
            />
          </div>

          {/* Car / CarPlay hint */}
          <div className="mt-6 sm:mt-8 mt-rise mt-rise-delay-4">
            <CarHint />
          </div>
        </div>
      </main>

      <footer
        className="relative z-10 px-6 sm:px-10 lg:px-14 py-6 border-t flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between"
        style={{ borderColor: "var(--mt-line)" }}
        data-testid="site-footer"
      >
        <div
          className="text-xs tracking-wide"
          style={{ color: "var(--mt-text-muted)" }}
        >
          © {new Date().getFullYear()} MADRETERRA radio · Una produzione di{" "}
          <a
            href={info.website_url}
            target="_blank"
            rel="noreferrer"
            className="mt-link-underline"
            style={{ color: "var(--mt-cream)" }}
          >
            Radio Madre Terra
          </a>
        </div>
        <div
          className="text-[11px] uppercase tracking-[0.25em]"
          style={{ color: "var(--mt-text-muted)" }}
        >
          Pace · Bene · Diretta
        </div>
      </footer>

      <InstallPrompt />
      <IosInstallGuide />
      <AndroidInstallGuide />
      <SleepTimer />
    </div>
  );
}

const InfoTile = ({ icon, label, value, href, testid, accent }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    data-testid={testid}
    className="mt-btn group flex items-center gap-4 rounded-xl border px-4 py-4"
    style={{
      borderColor: accent ? "rgba(184,85,48,0.40)" : "var(--mt-line)",
      background: accent ? "rgba(184,85,48,0.10)" : "rgba(20,17,13,0.45)",
    }}
  >
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
      style={{
        background: accent ? "rgba(184,85,48,0.18)" : "rgba(212,160,91,0.12)",
        color: accent ? "#f4a98a" : "var(--mt-amber-bright)",
        border: "1px solid rgba(212,160,91,0.20)",
      }}
    >
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div
        className="text-[10px] uppercase tracking-[0.28em]"
        style={{ color: "var(--mt-text-muted)" }}
      >
        {label}
      </div>
      <div
        className="font-display text-base sm:text-lg leading-tight truncate"
        style={{ color: "var(--mt-cream)" }}
      >
        {value}
      </div>
    </div>
    <ExternalLink
      size={15}
      strokeWidth={2.2}
      className="opacity-50 group-hover:opacity-100 transition-opacity"
      style={{ color: "var(--mt-cream)" }}
    />
  </a>
);
